// English in, Chinese out — from what Mynah already ships, and nothing else.
//
// WHAT THIS IS. Not a machine translator. It searches 9,448 sentences that human beings translated
// (Tatoeba) and the glosses of 6,639 graded words, and hands back the closest real thing. Where it
// finds a match the result is BETTER than a model's: an actual sentence a person wrote, already
// carrying per-word pinyin, a recording in the shipped voice, and words the learner can tap.
//
// WHY NOT A MODEL. The same reason as the tutor (spike/tutor.html): Mynah has to work offline and
// inside mainland China, where the major providers are unreachable. A translator that needs a
// network is a translator that fails in the country where the language is spoken.
//
// WHAT IT CANNOT DO, and must say so. It cannot compose a sentence nobody has written. Asked for
// something outside the corpus it has to admit that rather than return the nearest sentence about
// something else — a learner who cannot read Chinese has no way to tell a wrong translation from a
// right one, which is exactly why they asked.

import type { Sentence, Word } from './types.js';

/**
 * Contractions, expanded before matching.
 *
 * The corpus writes "I don't understand" and a learner types "I do not understand", or the reverse.
 * Stripping the apostrophe alone leaves "dont", which matches neither.
 */
const CONTRACTIONS: Record<string, string> = {
  im: 'i am', ive: 'i have', ill: 'i will', id: 'i would', lets: 'let us',
  youre: 'you are', youve: 'you have', youll: 'you will',
  hes: 'he is', shes: 'she is', its: 'it is', theyre: 'they are', theyve: 'they have',
  were: 'we are', weve: 'we have', thats: 'that is', whats: 'what is', wheres: 'where is',
  whos: 'who is', hows: 'how is', theres: 'there is',
  dont: 'do not', doesnt: 'does not', didnt: 'did not', cant: 'cannot', couldnt: 'could not',
  wont: 'will not', wouldnt: 'would not', shouldnt: 'should not', isnt: 'is not',
  arent: 'are not', wasnt: 'was not', werent: 'were not', havent: 'have not', hasnt: 'has not',
};

/** Words that say nothing about which sentence is wanted. */
const STOP = new Set(['a', 'an', 'the', 'to', 'of', 'is', 'are', 'am', 'do', 'does', 'did', 'it']);

/**
 * Words that reverse a sentence's meaning.
 *
 * Bag-of-words scoring cannot see these: "do you have any money" and "You don't have any money"
 * share every content word, so the negated sentence scored a perfect match and was offered as the
 * translation. A learner who cannot read the reply has no way to catch that. So a mismatch here
 * disqualifies outright rather than merely costing points.
 */
const NEGATIONS = new Set(['not', 'no', 'never', 'none', 'nothing', 'nobody', 'cannot', 'without']);

function negated(terms: Iterable<string>): boolean {
  for (const t of terms) if (NEGATIONS.has(t)) return true;
  return false;
}

/**
 * A crude stem, so "thanks" finds "thank" and "costs" finds "cost".
 *
 * Deliberately shallow. A real stemmer would collapse more pairs and also more distinctions, and
 * the cost of a wrong collapse here is a confidently wrong translation.
 */
export function stem(word: string): string {
  if (word.length <= 3) return word;
  for (const suffix of ['ing', 'ies', 'es', 'ed', 's']) {
    if (word.endsWith(suffix) && word.length - suffix.length >= 3) {
      const base = word.slice(0, -suffix.length);
      return suffix === 'ies' ? `${base}y` : base;
    }
  }
  return word;
}

/** Lowercase, drop punctuation, expand contractions. The comparable form of a phrase. */
export function normalise(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/gu, '')
    .replace(/\s+/gu, ' ')
    .trim()
    .split(' ')
    .map((w) => CONTRACTIONS[w] ?? w)
    .join(' ');
}

/** Content words of a phrase, stemmed, in order, with stop words dropped. */
export function tokens(text: string): string[] {
  return normalise(text)
    .split(' ')
    .filter((w) => w && !STOP.has(w))
    .map(stem);
}

export interface Index {
  /** Normalised English -> sentence position, for exact hits. */
  exact: Map<string, number>;
  /** Sentence position -> its stemmed content words. */
  docs: { at: number; terms: Set<string> }[];
  /** How many sentences each term appears in, for weighting. */
  df: Map<string, number>;
  total: number;
}

export function buildIndex(sentences: Sentence[]): Index {
  const exact = new Map<string, number>();
  const docs: Index['docs'] = [];
  const df = new Map<string, number>();
  sentences.forEach((s, at) => {
    const key = normalise(s.en);
    if (key && !exact.has(key)) exact.set(key, at);
    const terms = new Set(tokens(s.en));
    docs.push({ at, terms });
    for (const t of terms) df.set(t, (df.get(t) ?? 0) + 1);
  });
  return { exact, docs, df, total: sentences.length };
}

export interface Match {
  /** Position of the sentence in the shipped corpus. */
  at: number;
  score: number;
  exact: boolean;
}

/**
 * Every content word asked for must appear. Not most of them — all of them.
 *
 * Ranking alone is not enough here: every query has a best match, and for a phrase the corpus does
 * not contain, the best match is a sentence about something else. A learner who cannot read Chinese
 * has no way to catch that.
 *
 * Weighted partial coverage was not enough either, and failed in the worst way. "Do you have any
 * money" came back as 你有朋友吗 — "Do you have any friends?" — because three of the four words
 * matched and the weighting still cleared the bar. The word that carried the entire meaning was the
 * missing one. Demanding all of them turns near-misses into an honest "I cannot say that", which is
 * the right answer when the corpus does not contain it.
 */
export const REQUIRE_ALL_TERMS = true;

/**
 * How much of the SENTENCE may be things the learner did not ask for.
 *
 * Coverage alone asks "does the sentence contain what I said" and says nothing about what else it
 * contains. That let "I am sorry" return 抱歉，我没钱 ("Sorry, I don't have any money") and "the
 * food is delicious" return a sentence about a cafeteria's prices — both contain every word asked
 * for and both say a good deal more. A translation that says more than you meant is a wrong
 * translation.
 */
export const MIN_PRECISION = 0.5;

/**
 * Sentences that say what the query says, best first.
 *
 * Terms are weighted by how rare they are across the corpus, so "hungry" counts for far more than
 * "very": sharing a common word with a sentence is almost no evidence, and sharing a rare one is
 * nearly conclusive. Shorter sentences win ties, because a learner asking how to say something
 * wants the plainest way to say it.
 */
export function search(index: Index, sentences: Sentence[], query: string, limit = 5): Match[] {
  const wanted = tokens(query);
  if (!wanted.length) return [];

  const exactAt = index.exact.get(normalise(query));
  const out: Match[] = [];
  const seen = new Set<number>();
  if (exactAt !== undefined) {
    out.push({ at: exactAt, score: Infinity, exact: true });
    seen.add(exactAt);
  }

  const weight = (term: string) => Math.log(index.total / (1 + (index.df.get(term) ?? 0)));
  const asked = new Set(wanted);
  const askedWeight = [...asked].reduce((n, t) => n + weight(t), 0) || 1;

  const askedNegated = negated(asked);
  const scored: Match[] = [];
  for (const doc of index.docs) {
    if (seen.has(doc.at)) continue;
    // A sentence that negates what was asked (or fails to negate what was) means the opposite.
    if (negated(doc.terms) !== askedNegated) continue;
    let covered = 0;
    let missing = 0;
    for (const t of asked) {
      if (doc.terms.has(t)) covered += weight(t);
      else missing++;
    }
    if (REQUIRE_ALL_TERMS && missing > 0) continue;
    const recall = covered / askedWeight;
    // …and how much of the sentence is answer rather than extra.
    const precision = asked.size / Math.max(asked.size, doc.terms.size);
    if (precision < MIN_PRECISION) continue;
    scored.push({ at: doc.at, score: recall * precision, exact: false });
  }

  scored.sort(
    (a, b) => b.score - a.score || sentences[a.at]!.w.length - sentences[b.at]!.w.length || a.at - b.at,
  );

  // AN EXACT ENGLISH MATCH IS NOT ALWAYS THE PHRASE TO TEACH. "I'm hungry!" is in the corpus as
  // 我饥饿！, which is a real sentence made of words this course teaches — and 饥饿 is the 9,185th
  // commonest word where 饿 is in the first few hundred. A learner asking how to say it wants 我好饿，
  // which says everything asked in words they already have.
  //
  // So the exact hit yields to a scored one that ALSO says everything asked — score 1 is full
  // recall at full precision, not a partial match — and says it in commoner words. Anything less
  // than that leaves the exact match where it was: this is about two complete answers, not about
  // preferring an approximate one (MYN-184, when a fuller corpus first offered both).
  const best = scored[0];
  if (out.length && best && best.score >= 1 && (sentences[best.at]!.r ?? 0) < (sentences[out[0]!.at]!.r ?? 0)) {
    scored.shift();
    out.unshift({ ...best, exact: false });
  }
  return [...out, ...scored].slice(0, limit);
}

export interface WordMatch {
  /** Position of the word in the vocabulary. */
  at: number;
  /** The gloss that matched. */
  sense: string;
  exact: boolean;
}

/**
 * Vocabulary words whose meaning is what was typed.
 *
 * Separate from the sentence search because they answer different questions: "how do I say I am
 * hungry" wants a sentence, "how do I say hungry" wants a word. Both are worth showing, and which
 * one the learner meant is usually obvious to them and not to us.
 */
export function lookupWords(words: Word[], query: string, limit = 8): WordMatch[] {
  const q = normalise(query);
  if (!q) return [];
  const qStem = tokens(query).join(' ');
  const out: WordMatch[] = [];

  words.forEach((word, at) => {
    for (const gloss of word.e) {
      for (const raw of gloss.replace(/\([^)]*\)/gu, ' ').split(/[;,]/u)) {
        const sense = raw.trim();
        if (!sense) continue;
        const n = normalise(sense);
        if (!n) continue;
        // "to eat" should be found by "eat", and "hungry" by "hungry".
        const bare = n.replace(/^to /u, '');
        if (n === q || bare === q) {
          out.push({ at, sense, exact: true });
          return;
        }
        if (tokens(sense).join(' ') === qStem && qStem) {
          out.push({ at, sense, exact: false });
          return;
        }
      }
    }
  });

  // Commonest first: a learner asking how to say a word wants the word people actually use.
  return out.sort((a, b) => Number(b.exact) - Number(a.exact) || words[a.at]!.r - words[b.at]!.r).slice(0, limit);
}
